// docs/87 — Settings › Hotkeys: every hotkey in the game, grouped, with its keys. Click a key to
// change it, + to add another, × to drop one. A key already used by a hotkey that is live at the
// same time moves to the new one (the line under the search says where it came from), so two
// hotkeys never fight over a key. Rebinds write `settings.hotkeys` and apply at once.
import { useEffect, useMemo, useState } from 'react';
import { Button, Input } from '../components';
import { useSettings } from '../../core/stores/settings';
import {
  HOTKEYS, HOTKEY_GROUPS, bindKey, bindingsOf, chordLabel, chordOf, hotkeyDef, isModifierCode, isRebound, resetKey, setHotkeyCapture, unbindKey,
  useHotkeyOverrides, type HotkeyDef,
} from '../../core/input/hotkeys';

/** The key being changed: hotkey `id`, its key at `at` (or a new one when `at` is past the end). */
interface Capture { id: string; at: number }

const GROUP_HINT: Partial<Record<string, string>> = {
  Planning: 'On the board while you plan a turn.',
  Camera: 'The board and the overworld chart. Held keys keep panning or zooming.',
  Hotbar: 'The number row picks the action drawn in that place on the bar.',
  Story: 'Story pages, cinematics, cards and village talk.',
  Emotes: 'Hold the wheel key, then press an emote’s key. The emote keys only work while the wheel is open.',
};

export function HotkeysPanel() {
  const overrides = useHotkeyOverrides();
  const set = useSettings((s) => s.set);
  const [capture, setCapture] = useState<Capture | undefined>();
  const [note, setNote] = useState('');
  const [find, setFind] = useState('');

  const names = (ids: string[]) => ids.map((t) => hotkeyDef(t)?.label ?? t).join(', ');
  const save = (next: Record<string, string[]>, note = '') => { set({ hotkeys: next }); setNote(note); };

  // while waiting for a key, every key press is the new binding (Esc cancels; a click elsewhere too)
  useEffect(() => {
    if (!capture) return;
    setHotkeyCapture(true);
    const def = hotkeyDef(capture.id);
    const onDown = (e: KeyboardEvent) => {
      e.preventDefault(); e.stopImmediatePropagation();
      if (e.repeat) return;
      if (e.code === 'Escape' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) { setCapture(undefined); return; }
      // a held hotkey takes a bare key (a modifier on its own too); anything else waits for the key after the modifiers
      if (!def?.hold && isModifierCode(e.code)) return;
      const chord = def?.hold ? e.code : chordOf(e);
      if (!e.code) return;
      const r = bindKey(useSettings.getState().hotkeys ?? {}, capture.id, chord, capture.at);
      save(r.overrides, r.taken.length ? `${chordLabel(chord)} was taken from ${names(r.taken)}.` : '');
      setCapture(undefined);
    };
    const swallow = (e: KeyboardEvent) => { e.stopImmediatePropagation(); };
    const away = (e: PointerEvent) => { if (!(e.target as Element | null)?.closest?.('[data-hotkey-capture]')) setCapture(undefined); };
    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', swallow, true);
    window.addEventListener('pointerdown', away, true);
    return () => {
      // the key-up of the captured key comes after; let it pass before hotkeys fire again
      setTimeout(() => setHotkeyCapture(false), 0);
      window.removeEventListener('keydown', onDown, true);
      window.removeEventListener('keyup', swallow, true);
      window.removeEventListener('pointerdown', away, true);
    };
  }, [capture]);
  useEffect(() => () => setHotkeyCapture(false), []);

  const q = find.trim().toLowerCase();
  const shown = useMemo(() => HOTKEYS.filter((h) => !q || h.label.toLowerCase().includes(q) || h.group.toLowerCase().includes(q)
    || bindingsOf(h.id, overrides).some((c) => chordLabel(c).toLowerCase() === q)), [q, overrides]);
  const anyRebound = Object.keys(overrides).length > 0;

  return (
    <div className="fk-hotkeys" data-testid="settings-hotkeys">
      <div className="fk-hotkeys__top">
        <Input small value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a hotkey or a key" aria-label="Find a hotkey" data-testid="hotkeys-find" />
        <Button small variant="ghost" disabled={!anyRebound} onClick={() => save({})} data-testid="hotkeys-defaults">Restore default keys</Button>
      </div>
      <p className="fk-field__hint fk-hotkeys__help">Click a key to change it, then press the new one (Esc cancels). <b>+</b> adds another key, <b>×</b> removes one. A key that another hotkey uses at the same time moves to this one.</p>
      <p className="fk-hotkeys__note" role="status" aria-live="polite" data-testid="hotkeys-note">{note}</p>
      {HOTKEY_GROUPS.map((g) => {
        const rows = shown.filter((h) => h.group === g);
        if (!rows.length) return null;
        return (
          <section key={g} className="fk-hotkeys__group" aria-label={g}>
            <h4 className="fk-hotkeys__head">{g}{GROUP_HINT[g] && <span className="fk-field__hint"> · {GROUP_HINT[g]}</span>}</h4>
            {rows.map((h) => (
              <HotkeyRow key={h.id} def={h} keys={bindingsOf(h.id, overrides)} rebound={isRebound(h.id, overrides)} capture={capture?.id === h.id ? capture.at : undefined}
                onCapture={(at) => { setNote(''); setCapture({ id: h.id, at }); }}
                onRemove={(c) => save(unbindKey(overrides, h.id, c))}
                onReset={() => { const r = resetKey(overrides, h.id); save(r.overrides, r.taken.length ? `${h.label}'s keys were taken back from ${names(r.taken)}.` : ''); }} />
            ))}
          </section>
        );
      })}
      {!shown.length && <p className="fk-muted">No hotkey matches “{find}”.</p>}
    </div>
  );
}

function HotkeyRow({ def, keys, rebound, capture, onCapture, onRemove, onReset }: {
  def: HotkeyDef; keys: readonly string[]; rebound: boolean; capture?: number;
  onCapture: (at: number) => void; onRemove: (chord: string) => void; onReset: () => void;
}) {
  const adding = capture !== undefined && capture >= keys.length;
  return (
    <div className={`fk-hotkeys__row ${rebound ? 'is-rebound' : ''}`} data-testid="hotkey-row" data-hotkey={def.id}>
      <span className="fk-hotkeys__label">{def.label}{def.hold && <span className="fk-hotkeys__hold" title="Acts while held"> hold</span>}</span>
      <span className="fk-hotkeys__keys">
        {keys.map((c, i) => (
          <span key={c} className="fk-hotkeys__chip">
            <button type="button" className={`fk-hotkeys__key fk-kbd ${capture === i ? 'is-waiting' : ''}`} data-hotkey-capture onClick={() => onCapture(i)}
              aria-label={`${def.label}: ${chordLabel(c)}. Change`} data-testid="hotkey-key" data-chord={c}>{capture === i ? 'Press a key…' : chordLabel(c)}</button>
            <button type="button" className="fk-hotkeys__drop" onClick={() => onRemove(c)} aria-label={`Remove ${chordLabel(c)} from ${def.label}`} data-testid="hotkey-remove">×</button>
          </span>
        ))}
        {!keys.length && !adding && <span className="fk-faint fk-hotkeys__none">unbound</span>}
        {adding
          ? <span className="fk-hotkeys__key fk-kbd is-waiting" data-hotkey-capture data-testid="hotkey-waiting">Press a key…</span>
          : <button type="button" className="fk-hotkeys__add" data-hotkey-capture onClick={() => onCapture(keys.length)} aria-label={`Add a key for ${def.label}`} data-testid="hotkey-add">+</button>}
      </span>
      <button type="button" className="fk-hotkeys__reset" disabled={!rebound} onClick={onReset} title={`Back to ${def.defaults.map(chordLabel).join(', ') || 'unbound'}`} data-testid="hotkey-reset">Reset</button>
    </div>
  );
}
